import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { baseDeepClone } from '../helpers/base-deep-clone';
import { BaseListFilterMemoryService } from './base-list-filter-memory.service';
import { stripNonFilterParams } from './base-list-filter-memory.util';
import { ListFilterParams } from '../../_store/actions/base-list-filters.actions';

@Injectable({ providedIn: 'root' })
export class BaseQueryParamService {
  filterSubject = new Subject<{ page?: number; filters?: any } | null>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private listFilterMemory: BaseListFilterMemoryService
  ) {}

  /**
   * Get query params from ActivatedRoute snapshot
   * and convert comma separated strings to array.
   */
  getAllQueryParams(notArrayKeys: string[] = []): Params {
    return this.splitCommaSeparatedParams(this.route.snapshot.queryParams, notArrayKeys);
  }

  /**
   * Source of filter values a list should hydrate its form from on entry.
   * If a filter selection was remembered for this URL, it takes precedence over
   * the current query string (so the last active filters are restored); otherwise
   * the live query params are used. See {@link BaseListFilterMemoryService}.
   */
  getInitialFilterParams(notArrayKeys: string[] = []): Params {
    const snapshot = this.listFilterMemory.getSnapshot(this.getCurrentUrlPath());
    if (snapshot && Object.keys(snapshot).length) {
      return this.splitCommaSeparatedParams(snapshot, notArrayKeys);
    }

    return this.getAllQueryParams(notArrayKeys);
  }

  /**
   * If a filter selection was remembered for this URL, push it through the
   * filter pipeline so the list writes it back to the query string and refetches.
   * Called by {@link BaseFilterService.setInitialFilterValuesByQueryParams} on
   * list entry, right after the filters form has been seeded from the snapshot,
   * keeping the URL and fetched data in sync with the restored form.
   */
  syncRememberedFiltersToUrl(): void {
    const snapshot = this.listFilterMemory.getSnapshot(this.getCurrentUrlPath());
    if (snapshot && Object.keys(snapshot).length) {
      this.filterSubject.next({ filters: snapshot, page: 1 });
    }
  }

  private splitCommaSeparatedParams(source: Params, notArrayKeys: string[] = []): Params {
    const queryParams = baseDeepClone(source);
    if (Object.entries(queryParams).length) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (notArrayKeys.includes(key)) {
          return;
        }
        queryParams[key] = (value as string).split(',');
      });
    }

    return queryParams;
  }

  private getCurrentUrlPath(): string {
    return this.router.url.split('?')[0];
  }

  private persistCurrentFilters(queryParams: Params | null): void {
    this.listFilterMemory.save(this.getCurrentUrlPath(), stripNonFilterParams(queryParams as ListFilterParams));
  }

  /**
   * Get query params from ActivatedRoute snapshot
   * and convert comma separated strings to array.
   */
  getQueryParamByName(name: string): any {
    return this.route.snapshot.queryParams[name];
  }

  /**
   * Add a single query parameter and using "merge" for query params handling.
   * @param key
   * @param value
   * @param callback
   */
  addParameter(key: string, value: string, callback?: () => void) {
    if (!key || !value) {
      return;
    }

    const queryParams = { ...this.route.snapshot.queryParams, ...{ [key]: value } };

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      })
      .then(() => {
        if (callback) {
          callback();
        }
      });
  }

  /**
   * Remove a single query parameter from ActivatedRoute snapshot.
   * @param key
   * @param callback
   */
  removeParameter(key: string, callback?: () => void) {
    if (!key) {
      return;
    }

    const queryParams = { ...this.route.snapshot.queryParams };

    delete queryParams[key];

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
      })
      .then(() => {
        if (callback) {
          callback();
        }
      });
  }

  convertFiltersValuesToQueryParams(data: Object, objectValueFields: Record<string, string> = {}): Params | null {
    if (!data) {
      return null;
    }

    if (!Object.entries(data)?.length) {
      return null;
    }

    const queryParams: any = {};

    Object.entries(data).forEach(([key, value]) => {
      // Handle arrays
      if (Array.isArray(value) && value.length) {
        const filteredValues = value.map((val: any) => val.value || val.id).filter(Boolean);

        if (filteredValues.length) {
          queryParams[key] = filteredValues.toString();
        }
        return;
      }

      // Handle objects
      if (value !== null && typeof value === 'object') {
        const objectValue = objectValueFields[key];
        if (objectValue && value[objectValue] !== null) {
          queryParams[key] = value[objectValue];
        }
        return;
      }

      // Handle primitive values (non-object and non-array)
      if (value) {
        queryParams[key] = value;
      }
    });

    return queryParams;
  }

  updateQueryParams(data: Object, objectValueFields: Record<string, string> = {}) {
    if (!Object.entries(data)?.length) {
      return;
    }

    const queryParams: any = {};
    Object.entries(data).forEach(([key, value]) => {
      queryParams[key] = null;
      if (Array.isArray(value) && value.length) {
        queryParams[key] = [];
        value.forEach((val: any) => {
          if (val.value) {
            queryParams[key].push(val.value);
          } else if (val.id) {
            queryParams[key].push(val.id);
          } else {
            return;
          }
        });

        if (queryParams[key].toString()) {
          queryParams[key] = queryParams[key].toString();
        } else {
          delete queryParams[key];
        }
      } else if (value !== null && typeof value === 'object') {
        if (objectValueFields[key] && value[objectValueFields[key]] !== null) {
          queryParams[key] = value[objectValueFields[key]];
        } else {
          return;
        }
      } else {
        if (!value) {
          queryParams[key] = null;
          return;
        }

        queryParams[key] = value;
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  redirectWithQueryParams(
    filtersFormValue: { page?: number; filters?: any } | null,
    objectValueFields: Record<string, string> = {},
    callback?: () => void
  ) {
    const queryParams = this.convertFiltersValuesToQueryParams(
      {
        ...this.route.snapshot.queryParams,
        ...filtersFormValue?.filters,
        page: filtersFormValue?.page?.toString(),
      },
      objectValueFields
    );

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams,
      })
      .then(() => {
        this.persistCurrentFilters(queryParams);
        if (callback) {
          callback();
        }
      });
  }

  getQueryParam(key: string): string {
    if (!key) {
      return '';
    }

    return this.route.snapshot.queryParams[key] ? this.route.snapshot.queryParams[key] : '';
  }
}
